/**
 * Page copy that was previously inline in HTML or (for the carousel) in a
 * page-local inline <script>. Extracted so the FAQ list can be the single
 * source of truth for both the rendered accordion and the FAQPage JSON-LD.
 */

export interface Faq {
  readonly q: string;
  readonly a: string;
}

/**
 * Home-page FAQ. This array drives BOTH the visible accordion and the
 * FAQPage structured data, so the two can no longer drift apart (the original
 * had them duplicated by hand in two places).
 */
export const FAQS: readonly Faq[] = [
  {
    q: 'What is a tax equity investment?',
    a: 'Tax equity investing lets you offset your tax liability by investing in qualifying energy programs. Instead of simply paying what you owe, your capital is redirected into renewable energy infrastructure, generating both cash savings and long-term passive income.',
  },
  {
    q: 'What is the Investment Tax Credit (ITC), and how much can I save?',
    a: "The ITC is a federal tax credit that rewards investment in renewable energy projects. Through Lux Financial's approach, combining ITC benefits with strategic program selection, clients can achieve up to 40%+ in total cash savings.",
  },
  {
    q: 'Who is this for? Do I need to be a high-net-worth individual?',
    a: "Tax equity investing is most effective when targeting high tax liability, but it isn't exclusive to high-net-worth individuals. We work with anyone carrying a substantial tax burden each year, as well as the CPAs, wealth advisors, and family offices who represent them.",
  },
  {
    q: 'I already work with a CPA or wealth advisor. Does this replace them?',
    a: "No, we work alongside your existing advisors, not in competition with them. Lux Financial integrates directly with your CPA or advisor's existing strategy so you stay with the professionals you trust while we handle the tax equity and energy investment side.",
  },
  {
    q: 'What happens after I schedule a consultation?',
    a: 'A team member reaches out within 24\u201348 hours to schedule a personalized consultation, where we walk through your specific tax situation and determine which energy investment programs fit your goals.',
  },
  {
    q: "How do I track my investment once I'm in?",
    a: "Our proprietary technology platform gives you real-time visibility into your portfolio's performance and ROI, with full transparency into every investment, return, and tax benefit, rather than a once-a-year report.",
  },
  {
    q: 'Is this just a one-time tax write-off, or ongoing income?',
    a: 'Both. You get the upfront ITC-driven cash savings, and the energy programs themselves are structured to produce ongoing passive income as demand for power continues to grow.',
  },
] as const;

export interface ProcessSlide {
  readonly title: string;
  readonly desc: string;
  readonly img: string;
  readonly alt: string;
}

/** Home-page process carousel — was the SLIDES array in an inline script. */
export const PROCESS_SLIDES: readonly ProcessSlide[] = [
  {
    title: 'Personalized Tax Consultation',
    desc: 'We begin with a thorough review of your current tax position and identify opportunities for significant savings.',
    img: 'images/process-consultation.webp',
    alt: 'Advisor and client shaking hands during a personalized tax consultation',
  },
  {
    title: 'Review Available Programs',
    desc: 'Our team presents tailored energy investment programs aligned with your financial goals and risk profile.',
    img: 'images/process-programs.webp',
    alt: 'Solar energy investment program under a clear sky',
  },
  {
    title: 'Welcome Meeting',
    desc: 'A dedicated onboarding experience ensures complete understanding and alignment with your investment strategy.',
    img: 'images/process-welcome.webp',
    alt: 'Advisor and client reviewing the investor portal together during onboarding',
  },
  {
    title: 'Save & Build Passive Income',
    desc: 'Begin realizing up to 40%+ in cash savings while your investments generate ongoing passive income streams.',
    img: 'images/process-passive-income.webp',
    alt: 'Investors celebrating their portfolio growth with a skyline view',
  },
] as const;

export interface NewsItem {
  readonly source: string;
  readonly date: string;
  readonly title: string;
  readonly excerpt: string;
  readonly href: string;
}

export const NEWS_ITEMS: readonly NewsItem[] = [
  {
    source: 'GlobeNewswire',
    date: 'November 18, 2025',
    title: 'Lux Financial to Offset $2B in Tax Equity Investments',
    excerpt:
      'Lux Financial announced plans to offset approximately $2 billion in tax equity investments for its partners over the next few years, signaling its growing presence in the renewable energy tax credit market.',
    href: '/article-2b-offset',
  },
  {
    source: 'CEO Weekly',
    date: 'November 11, 2025',
    title: 'Lux Financial Unlocks the Hidden Power of Tax Equity Investments',
    excerpt:
      'Lux Financial has enabled investors, CPAs, and family offices to convert a significant portion of their federal tax obligations into renewable energy participation, with transferable tax credits having doubled to $20 billion year-to-date in 2025.',
    href: '/article-tax-equity',
  },
] as const;

export interface SelectOption {
  readonly value: string;
  readonly label: string;
}

export const AUDIENCE_OPTIONS: readonly SelectOption[] = [
  { value: 'investor', label: 'High Net Worth Investor' },
  { value: 'cpa', label: 'CPA / Tax Advisor' },
  { value: 'wealth', label: 'Wealth Advisor' },
  { value: 'family-office', label: 'Family Office' },
  { value: 'other', label: 'Other' },
] as const;

export const REFERRAL_OPTIONS: readonly SelectOption[] = [
  { value: 'referral', label: 'Referral from a CPA / Advisor' },
  { value: 'referral-client', label: 'Referral from a Client' },
  { value: 'google', label: 'Google Search' },
  { value: 'social', label: 'Social Media' },
  { value: 'event', label: 'Event / Conference' },
  { value: 'press', label: 'News / Press' },
  { value: 'other', label: 'Other' },
] as const;
